import { GameSession } from "./game-session.js";

// A separate, uniquely solvable practice board. Journey saves never use this ID.
export const TUTORIAL_PUZZLE = {
  id: "learn-to-play",
  difficulty: "easy",
  size: 4,
  regions: [0, 1, 2, 1, 0, 1, 1, 1, 0, 0, 3, 1, 0, 3, 3, 1],
  solution: [2, 4, 11, 13],
  givens: [],
};

const STEPS = [
  {
    title: "Let's find a cat",
    text: "Double-tap the green tile. On a computer, double-click. There's only one home in this color.",
    action: "double", targets: [2], focus: [2],
  },
  {
    title: "One cat per color",
    text: "The green color has its cat. Every other color needs exactly one, too.",
    action: "continue", focus: [2], button: "Got it",
  },
  {
    title: "One cat in each row",
    text: "This row already has a cat. Tap the other three tiles once to cross them out.",
    action: "mark", targets: [0, 1, 3], focus: [0, 1, 2, 3],
  },
  {
    title: "One cat in each column",
    text: "The same goes for columns. Tap these three tiles to rule them out.",
    action: "mark", targets: [6, 10, 14], focus: [2, 6, 10, 14],
  },
  {
    title: "Cats need a little space",
    text: "Cats cannot touch, even at the corners. Cross out the two diagonal neighbors.",
    action: "mark", targets: [5, 7], focus: [2, 5, 7],
  },
  {
    title: "One home left",
    text: "Only one tile is left in the second row. Double-tap it to find the next cat.",
    action: "double", targets: [4], focus: [4, 5, 6, 7], autoMark: true,
  },
  {
    title: "A little help with crosses",
    text: "Automatic crosses ruled out the other pink tiles for you. This is on by default; you can change it in Settings.",
    action: "continue", focus: [0, 4, 8, 9, 12], autoMark: true, button: "Got it",
  },
  {
    title: "You're getting it",
    text: "The third row has one home left, too. Double-tap to find its cat.",
    action: "double", targets: [11], focus: [8, 9, 10, 11], autoMark: true,
  },
  {
    title: "Find the last cat",
    text: "One cat to go. Double-tap its last possible home to finish the puzzle.",
    action: "double", targets: [13], focus: [12, 13, 14, 15], autoMark: true,
  },
  {
    title: "You're ready!",
    text: "One cat per color, row, and column. No touching. The cat button can reveal a cat; the bulb adds safe crosses.",
    action: "finish", focus: [2, 4, 11, 13], autoMark: true, button: "Start game",
  },
];

/** Guided commands use real rules and input, with no lives or help spent outside practice. */
export class TutorialSession {
  constructor({ now = Date.now } = {}) {
    this.session = new GameSession(TUTORIAL_PUZZLE, { now });
    this.index = 0;
  }
  get step() { return STEPS[this.index]; }
  perform(command, index) {
    const step = this.step;
    if (!step.targets?.includes(index)) return {};
    const permitted = step.action === "double"
      ? ["mark", "promoteMark", "place"]
      : step.action === "mark" ? ["mark"] : [];
    if (!permitted.includes(command)) return {};
    if (step.action === "mark" && this.session.data.marks.includes(index)) return {};
    const effect = this.session[command](index);
    if (!effect.changed) return effect;
    const values = step.action === "double" ? this.session.data.cats : this.session.data.marks;
    if (step.targets.every((target) => values.includes(target))) {
      this.index += 1;
      return { ...effect, tutorialStep: true };
    }
    return effect;
  }
  next() {
    if (this.step.action !== "continue") return false;
    this.index += 1;
    return true;
  }
  snapshot() {
    const step = this.step;
    const done = step.action === "mark" ? this.session.data.marks : this.session.data.cats;
    const targets = (step.targets ?? []).filter((index) => !done.includes(index));
    return {
      ...step,
      index: this.index,
      total: STEPS.length,
      targets,
      hand: targets[0] ?? null,
      autoMark: Boolean(step.autoMark),
    };
  }
}
